import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { tursoAPI as propertyAPI } from '../services/tursoApi';
import {
  ChevronDown,
  ChevronUp,
  Grid,
  List,
  Search,
  X,
  SlidersHorizontal,
  Bed,
  Bath,
  Ruler,
  Car,
  CalendarDays,
  Loader2,
  MapPin,
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Helmet } from 'react-helmet-async';


gsap.registerPlugin(ScrollTrigger);

gsap.config({
  force3D: true,
  autoSleep: 60,
  nullTargetWarn: false,
});

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});


const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({
    propertyType: '',
    priceRange: '',
    bedrooms: '',
    bathrooms: '',
    location: '',
    area: '',
    parking: '',
    yearBuilt: '',
    amenities: [],
    status: '',
  });

  const [mapCenter, setMapCenter] = useState([15.9129, 79.7400]);
  const [mapZoom, setMapZoom] = useState(7);


  useEffect(() => {
    fetchProperties();
  }, [filters]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      console.log('Current filters:', filters);

      const query = {};

      if (filters.propertyType) {
        query.property_type = filters.propertyType.toLowerCase();
      }
      if (filters.status) {
        query.status = filters.status.toLowerCase();
      }
      if (filters.bedrooms) {
        query.bedrooms = filters.bedrooms;
      }
      if (filters.bathrooms) {
        query.bathrooms = filters.bathrooms;
      }
      if (filters.parking) {
        query.parking = filters.parking;
      }
      if (filters.location) {
        query.location = filters.location;
      }
      if (filters.priceRange) {
        const [min, max] = filters.priceRange.split('-');
        query.minPrice = min;
        query.maxPrice = max;
      }
      if (filters.area) {
        const [min, max] = filters.area.split('-');
        query.min_area = min;
        query.max_area = max;
      }
      if (filters.amenities && filters.amenities.length > 0) {
        query.amenities = filters.amenities;
      }

      const data = await propertyAPI.getAllProperties(query);
      setProperties(data.properties || []);
      console.log('Fetched properties:', data);

      if (data.properties && data.properties.length > 0) {
        const firstValidProperty = data.properties.find(p => p.location && p.location.coordinates && p.location.coordinates.lat && p.location.coordinates.lng);
        if (firstValidProperty) {
          setMapCenter([firstValidProperty.location.coordinates.lat, firstValidProperty.location.coordinates.lng]);
          setMapZoom(12);
        } else {
          setMapCenter([17.762173, 83.211819]);
          setMapZoom(5);
        }
      } else {
        setMapCenter([17.546434, 83.064069]);
        setMapZoom(5);
      }

    } catch (error) {
      console.error('Error fetching properties:', error);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleAmenityToggle = (amenity) => {
    setFilters((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter((a) => a !== amenity)
        : [...prev.amenities, amenity],
    }));
  };

  const clearFilters = () => {
    setFilters({
      propertyType: '',
      priceRange: '',
      bedrooms: '',
      bathrooms: '',
      location: '',
      area: '',
      parking: '',
      yearBuilt: '',
      amenities: [],
      status: '',
    });
  };

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(1)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(1)} L`;
    }
    return `₹${price.toLocaleString()}`;
  };

  const createPriceIcon = (price) => {
    const formattedPrice = formatPrice(price);
    return L.divIcon({
      className: 'custom-price-marker',
      html: `<div class="relative flex items-center justify-center px-3 py-1 bg-accent text-white rounded-full shadow-lg font-semibold text-sm whitespace-nowrap">
                ${formattedPrice}
              </div>`,
      iconSize: [60, 30],
      iconAnchor: [30, 15],
      popupAnchor: [0, -15]
    });
  };


  const PropertyCard = ({ property, viewMode }) => {
    const cardRef = useRef(null);

    useEffect(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: cardRef.current,
          start: 'top bottom-=100',
          toggleActions: 'play reverse play reverse',
        }
      });

      tl.fromTo(cardRef.current,
        { opacity: 0, y: 50, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'power3.out' }
      );

      return () => {
        if (tl.scrollTrigger) {
          tl.scrollTrigger.kill();
          tl.kill();
        }
      };
    }, [property.id]);

    return (
      <motion.div
        ref={cardRef}
        layout
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        whileHover={{
          scale: 1.03,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          transition: { duration: 0.3, ease: 'easeOut' },
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25,
          mass: 0.8,
        }}
        className={`relative bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100 transition-all duration-300 transform-gpu ${
          viewMode === 'list' ? 'flex flex-col md:flex-row' : ''
        }`}
        onMouseEnter={(e) => {
          gsap.to(e.currentTarget.querySelector('img'), {
            scale: 1.1,
            duration: 0.5,
            ease: 'power3.out',
          });
        }}
        onMouseLeave={(e) => {
          gsap.to(e.currentTarget.querySelector('img'), {
            scale: 1,
            duration: 0.4,
            ease: 'power3.out',
          });
        }}
      >
        <Link
          to={`/properties/${property.slug}`}
          className={`block group ${viewMode === 'list' ? 'flex flex-1 flex-col md:flex-row' : ''}`}
        >
          <div
            className={`relative ${
              viewMode === 'list' ? 'w-full md:w-96 lg:w-80 xl:w-96 h-64 md:h-auto flex-shrink-0' : 'h-72'
            } overflow-hidden rounded-t-2xl md:rounded-l-2xl md:rounded-tr-none`}
          >
            {property.images && property.images.length > 0 ? (
              <img
                src={property.images[0].url}
                alt={property.images[0].alt || 'Property image'}
                className="w-full h-full object-cover transition-transform duration-500 ease-out"
              />
            ) : (
              <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                No Image Available
              </div>
            )}
            <div className="absolute top-4 right-4 bg-accent text-white px-4 py-2 rounded-full text-sm font-semibold shadow-md">
              {property.status.charAt(0).toUpperCase() + property.status.slice(1)}
            </div>
            {property.featured && (
              <div className="absolute top-4 left-4 bg-yellow-500 text-gray-900 px-4 py-2 rounded-full text-sm font-semibold shadow-md">
                Featured
              </div>
            )}
          </div>
          <div
            className={`p-6 flex-1 ${
              viewMode === 'list' ? 'flex flex-col justify-between' : ''
            }`}
          >
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2 leading-tight transition-colors duration-300 group-hover:text-accent">
                {property.title}
              </h3>
              <p className="text-gray-600 mb-4 flex items-center">
                <MapPin size={18} className="mr-2 text-gray-500" />
                {property.location.address}, {property.location.city}
              </p>
            </div>
            <div
              className={`flex flex-col ${
                viewMode === 'list' ? 'mt-4' : ''
              }`}
            >
              <div className="text-2xl font-extrabold text-accent mb-4">
                {formatPrice(property.price)}
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-3 text-gray-700 text-base">
                <span className="flex items-center gap-2">
                  <Bed size={20} className="text-gray-500" />
                  {property.features.bedrooms} Beds
                </span>
                <span className="flex items-center gap-2">
                  <Bath size={20} className="text-gray-500" />
                  {property.features.bathrooms} Baths
                  </span>
                <span className="flex items-center gap-2">
                  <Ruler size={20} className="text-gray-500" />
                  {property.features.area} sqft
                </span>
                {property.features.parking > 0 && (
                  <span className="flex items-center gap-2">
                    <Car size={20} className="text-gray-500" />
                    {property.features.parking} Parking
                  </span>
                )}
                {property.features.yearBuilt > 0 && (
                  <span className="flex items-center gap-2">
                    <CalendarDays size={20} className="text-gray-500" />
                    Built {property.features.yearBuilt}
                  </span>
                )}
              </div>
            </div>
          </div>
        </Link>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-indigo-50">
      <Helmet>
        <title>Properties | Antacres</title>
        <meta 
          name="description" 
          content="Explore our wide range of properties for sale and rent in India. Find your dream home or investment property with us." 
        />
      </Helmet>

      <div className="relative h-[50vh] bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1560518883-ff51fcd2411e?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGenuDB8fHx8fA%3D%3D')" }}>
        <div className="absolute inset-0 bg-gradient-to-r from-accent to-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="text-5xl md:text-7xl font-extrabold font-display mb-4 drop-shadow-lg"
          >
            Find Your Dream Property
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
            className="text-lg md:text-xl text-white/90 max-w-2xl"
          >
            Browse through our exclusive collection of premium properties, meticulously curated for you.
          </motion.p>
        </div>
      </div>

      <div className=" ml-5 mr-5 -mt-7 relative z-20">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 p-4 bg-white rounded-2xl shadow-xl border border-gray-100 relative z-10">
          <div className="relative flex-1 min-w-[200px] max-w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
              placeholder="Location"
              className="w-full pl-10 pr-4 py-3 rounded-3xl border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-accent focus:border-accent shadow-sm text-gray-800 placeholder-gray-500"
            />
          </div>

          <div className="relative flex-1 min-w-[150px] max-w-[200px]">
            <select
              name="propertyType"
              value={filters.propertyType}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 rounded-3xl border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-accent focus:border-accent shadow-sm text-gray-800 appearance-none pr-8"
            >
              <option value="">Type</option>
              <option value="apartment">Apartment</option>
              <option value="duplex">Duplex</option>
              <option value="villa">Villa</option>
              <option value="commercial">Commercial</option>
              <option value="studio">Studio</option>
              <option value="house">House</option>
            </select>
            <ChevronDown size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative flex-1 min-w-[150px] max-w-[200px]">
            <select
              name="priceRange"
              value={filters.priceRange}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 rounded-3xl border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-accent focus:border-accent shadow-sm text-gray-800 appearance-none pr-8"
            >
              <option value="">Price Range</option>
              <option value="0-5000000">Under ₹50 Lac</option>
              <option value="5000000-10000000">₹50 Lac - ₹1 Cr</option>
              <option value="10000000-20000000">₹1 Cr - ₹2 Cr</option>
              <option value="20000000-999999999">Above ₹2 Cr</option>
            </select>
            <ChevronDown size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative flex-1 min-w-[120px] max-w-[160px]">
            <select
              name="bedrooms"
              value={filters.bedrooms}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 rounded-3xl border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-accent focus:border-accent shadow-sm text-gray-800 appearance-none pr-8"
            >
              <option value="">Beds</option>
              {[1, 2, 3, 4, 5].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
              <option value="5+">5+</option>
            </select>
            <ChevronDown size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          <div className="relative flex-1 min-w-[120px] max-w-[160px]">
            <select
              name="bathrooms"
              value={filters.bathrooms}
              onChange={handleFilterChange}
              className="w-full px-4 py-3 rounded-3xl border border-gray-200 bg-gray-50 focus:ring-2 focus:ring-accent focus:border-accent shadow-sm text-gray-800 appearance-none pr-8"
            >
              <option value="">Baths</option>
              {[1, 2, 3, 4].map((num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              ))}
              <option value="4+">4+</option>
            </select>
            <ChevronDown size={20} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
          <div className="relative flex-1 min-w-[120px] max-w-[160px]">
            <button
              onClick={() => {
                clearFilters();
                setIsMoreFiltersOpen(false);
              }}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-3xl hover:bg-gray-100 transition-colors duration-200 font-semibold"
            >
              Clear All
            </button>
          </div>


          <button
            onClick={() => setIsMoreFiltersOpen(!isMoreFiltersOpen)}
            className="flex items-center gap-2 px-5 py-3 bg-accent text-white rounded-xl shadow-md hover:bg-accent transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 flex-shrink-0"
          >
            <SlidersHorizontal size={20} />
            <span className="font-semibold">More Filters</span>
            {isMoreFiltersOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          <div className="flex items-center gap-6 mt-4 md:mt-0 flex-shrink-0">
            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1 shadow-inner">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'grid'
                    ? 'bg-accent text-white shadow'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
                title="Grid View"
              >
                <Grid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'list'
                    ? 'bg-accent text-white shadow'
                    : 'text-gray-700 hover:bg-gray-200'
                }`}
                title="List View"
              >
                <List size={20} />
              </button>
            </div>
            <div className="text-base font-medium text-gray-700">
              {properties.length} properties found
            </div>
          </div>
        </div>

        <AnimatePresence>
          {isMoreFiltersOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto transform-gpu"
              >
                <div className="p-8">
                  <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-200">
                    <h2 className="text-3xl font-bold text-gray-800">More Filters</h2>
                    <button
                      onClick={() => setIsMoreFiltersOpen(false)}
                      className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                      aria-label="Close filters"
                    >
                      <X size={28} />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 mb-8">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Area (sq.ft)
                      </label>
                      <select
                        name="area"
                        value={filters.area}
                        onChange={handleFilterChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-200 shadow-sm"
                      >
                        <option value="">Any</option>
                        <option value="0-1000">Under 1000</option>
                        <option value="1000-2000">1000 - 2000</option>
                        <option value="2000-3000">2000 - 3000</option>
                        <option value="3000+">Above 3000</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Parking Spots
                      </label>
                      <select
                        name="parking"
                        value={filters.parking}
                        onChange={handleFilterChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-200 shadow-sm"
                      >
                        <option value="">Any</option>
                        {[1, 2, 3].map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                        <option value="3+">3+</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        name="status"
                        value={filters.status}
                        onChange={handleFilterChange}
                        className="w-full px-4 py-2.5 rounded-lg border border-gray-300 bg-gray-50 focus:ring-2 focus:ring-accent focus:border-accent transition-all duration-200 shadow-sm"
                      >
                        <option value="">Any</option>
                        <option value="available">Available</option>
                        <option value="sold">Sold</option>
                        <option value="rented">Rented</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <label className="block text-lg font-bold text-gray-800 mb-4">
                      Amenities
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {[
                        'Swimming Pool', 'Gym', 'Garden', 'Security', 'Power Backup',
                        'Elevator', 'Club House', "Children's Play Area", 'Parking',
                        'Balcony', 'Pet Friendly', '24/7 Water Supply', 'Community Hall', 'Sports Facility'
                      ].map((amenity) => (
                        <label
                          key={amenity}
                          className="flex items-center space-x-3 text-gray-700 cursor-pointer hover:text-accent transition-colors duration-150 p-2 rounded-md hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={filters.amenities.includes(amenity)}
                            onChange={() => handleAmenityToggle(amenity)}
                            className="rounded text-accent focus:ring-accent h-5 w-5 border-gray-300"
                          />
                          <span className="text-base font-medium">{amenity}</span>
                        </label>
                      ))}
                  </div>
                </div>
                </div>

                <div className="flex justify-end items-center p-6 border-t border-gray-200 bg-gray-50 gap-4">
                  <button
                    onClick={() => {
                      clearFilters();
                      setIsMoreFiltersOpen(false);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-3xl hover:bg-gray-100 transition-colors duration-200 font-semibold"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => {
                      console.log('Applying filters:', filters);
                      setIsMoreFiltersOpen(false);
                    }}
                    className="px-8 py-3 bg-accent text-white rounded-3xl hover:bg-accent transition-colors duration-200 shadow-lg font-semibold transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                  >
                    Apply Filters
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex flex-col-reverse lg:flex-row gap-8">
          <div className={`${viewMode === 'list' ? 'lg:w-[60%]' : 'lg:w-1/2'}`}>
            {loading ? (
              <div className="flex flex-col justify-center items-center min-h-[400px] bg-white rounded-2xl shadow-xl p-8">
                <Loader2 className="animate-spin h-16 w-16 text-accent mb-4" />
                <p className="text-lg text-gray-700 font-medium">Loading properties...</p>
              </div>
            ) : properties.length === 0 ? (
              <div className="flex flex-col justify-center items-center min-h-[400px] bg-white rounded-2xl shadow-xl p-8">
                <img src="https://placehold.co/192x192/E0E0E0/333333?text=No+Properties" alt="No properties found" className="w-48 h-48 mb-4" />
                <h3 className="text-2xl font-bold text-gray-800 mb-2">No Properties Found</h3>
                <p className="text-gray-600 text-center max-w-md">
                  Your search did not yield any results. Please try adjusting your filters or
                  clearing them.
                </p>
                <button
                  onClick={clearFilters}
                  className="mt-6 px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent transition-colors duration-200 shadow-md font-semibold"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <motion.div
                layout
                className={`grid gap-8 ${
                  viewMode === 'grid'
                    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2'
                    : 'grid-cols-1'
                }`}
              >
                <AnimatePresence>
                  {properties.map((property) => (
                    <PropertyCard key={property.id} property={property} viewMode={viewMode} />
                  ))}
                </AnimatePresence>
              </motion.div>
            )}
          </div>

          <div
            className={`lg:w-1/2 h-80 md:h-96 lg:h-[calc(550vh-100px)] rounded-2xl overflow-hidden shadow-xl border border-gray-100 bg-white ${isMoreFiltersOpen ? 'hidden' : ''} ${viewMode === 'list' ? 'lg:w-[50%]' : 'lg:w-1/2'}`}
          >
            <MapContainer
              center={mapCenter}
              zoom={mapZoom}
              scrollWheelZoom={true}
              className="w-full h-full"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {properties.map((property) => (
                property.location && property.location.coordinates && property.location.coordinates.lat && property.location.coordinates.lng ? (
                  <Marker
                    key={property.id}
                    position={[property.location.coordinates.lat, property.location.coordinates.lng]}
                    icon={createPriceIcon(property.price)}
                  >
                    <Popup>
                      <div className="font-semibold text-accent">{property.title}</div>
                      <div className="text-sm text-gray-600">{property.location.address}, {property.location.city}</div>
                      <div className="text-md font-bold text-gray-800 mt-1">{formatPrice(property.price)}</div>
                      <Link to={`/properties/${property.slug}`} className="text-accent hover:underline text-sm mt-2 block">
                        View Details
                      </Link>
                    </Popup>
                  </Marker>
                ) : null
              ))}
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Properties;